import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import AlarmOutlinedIcon from '@mui/icons-material/AlarmOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import PauseCircleOutlineOutlinedIcon from '@mui/icons-material/PauseCircleOutlineOutlined';

/**
 * SummaryCard - Card individual de KPI (mesmo visual de CompanySummaryCards)
 */
function SummaryCard({ title, value, icon, iconColor, iconBg }) {
  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, fontSize: '0.8125rem' }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '1.75rem', color: 'text.primary', lineHeight: 1 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 20 } })}
        </Box>
      </Box>
    </Card>
  );
}

/**
 * SubscriptionSummaryCards - Cards de KPI no topo da tela "Assinaturas" (Admin Geral)
 * @param {Object} props
 * @param {Object} props.summary - retorno de SubscriptionOverviewQueries::summary()
 * @param {number} props.daysAhead - janela usada em "Trial vence em N dias"
 */
function SubscriptionSummaryCards({ summary, daysAhead }) {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const s = summary || {};

  const cards = [
    {
      title: 'Assinaturas ativas',
      value: s.active_subscriptions ?? 0,
      icon: <VerifiedOutlinedIcon />,
      iconColor: '#2E7D32',
      iconBg: '#E8F5E9',
    },
    {
      title: 'Em trial',
      value: s.in_trial ?? 0,
      icon: <HourglassEmptyOutlinedIcon />,
      iconColor: '#1565C0',
      iconBg: '#E3F2FD',
    },
    {
      title: `Trial vence em ${daysAhead} dias`,
      value: s.trial_expiring ?? 0,
      icon: <AlarmOutlinedIcon />,
      iconColor: '#E65100',
      iconBg: '#FFF3E0',
    },
    {
      title: 'Trial vencido sem assinatura',
      value: s.trial_expired_no_subscription ?? 0,
      icon: <EventBusyOutlinedIcon />,
      iconColor: '#C62828',
      iconBg: '#FFEBEE',
    },
    {
      title: 'Inadimplente',
      value: s.past_due ?? 0,
      icon: <ReportProblemOutlinedIcon />,
      iconColor: '#C62828',
      iconBg: '#FFEBEE',
    },
    {
      title: 'Cancelando',
      value: s.canceling ?? 0,
      icon: <PauseCircleOutlineOutlinedIcon />,
      iconColor: '#E65100',
      iconBg: '#FFF3E0',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)',
        gap: 2,
        mb: 3,
      }}
    >
      {cards.map((card, index) => (
        <SummaryCard key={index} {...card} />
      ))}
    </Box>
  );
}

export default SubscriptionSummaryCards;
